import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/driver-theme.css";

export type ResolvedTourStep = {
  target?: string;
  title: string;
  description: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
};

export type TourLabels = {
  next: string;
  prev: string;
  done: string;
  /** Label for the non-destructive exit button ("Maybe later"). */
  skip: string;
  /** Driver.js progress template, e.g. "{{current}} / {{total}}" — the {{…}} tokens must survive i18n. */
  progress: string;
};

export type RunTourOptions = {
  steps: ResolvedTourStep[];
  labels: TourLabels;
  onComplete?: () => void;
  onSkip?: () => void;
};

export type TourHandle = { destroy: () => void };

/**
 * Build + start a Driver.js tour from resolved (already-translated) steps.
 * - Steps whose `target` selector is not in the DOM are dropped (graceful skip; never crashes the page).
 * - The popover is NOT dismissible by outside click / ESC / an X icon; the only exit is the injected
 *   "Maybe later" button, which marks the tour skipped.
 * - Distinguishes "completed" (reached the final Done) from "skipped" ("Maybe later").
 * Returns null when no usable step remains (e.g. every target is missing).
 */
export function runTour(options: RunTourOptions): TourHandle | null {
  if (typeof document === "undefined") return null;

  // Resolve a selector to the FIRST visible matching element (rendered box > 0). The same anchor can exist
  // on both the desktop top tab and the mobile bottom tab — only one is visible per viewport, so we target
  // that one. Steps whose target is absent or hidden (no permission, off-screen nav, missing widget) resolve
  // to null and are dropped gracefully, so the tour never highlights nothing or crashes.
  const resolveVisibleTarget = (selector: string): Element | null => {
    const elements = Array.from(document.querySelectorAll(selector));
    return (
      elements.find((element) => {
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) ?? null
    );
  };

  type ResolvedEntry = { step: ResolvedTourStep; element: Element | undefined };
  const resolved = options.steps
    .map((step): ResolvedEntry | null => {
      if (!step.target) return { step, element: undefined };
      const element = resolveVisibleTarget(step.target);
      return element ? { step, element } : null;
    })
    .filter((entry): entry is ResolvedEntry => entry !== null);

  if (resolved.length === 0) return null;

  const steps: DriveStep[] = resolved.map(({ step, element }) => {
    const isSidebarTarget = Boolean(element?.closest("[data-sidebar='sidebar']"));

    return {
      element,
      // Prevent clicking through a highlighted nav item (which would navigate away and break the tour).
      disableActiveInteraction: true,
      popover: {
        title: step.title,
        description: step.description,
        side: isSidebarTarget ? "right" : step.side,
        align: isSidebarTarget ? "start" : step.align
      }
    };
  });

  let completed = false;
  const instance = driver({
    showProgress: resolved.length > 1,
    progressText: options.labels.progress,
    // Not dismissible by outside click, ESC, or an X icon — exit only via the "Maybe later" button.
    allowClose: false,
    overlayClickBehavior: () => undefined,
    showButtons: ["next", "previous"],
    smoothScroll: true,
    // Use the current design-system overlay token, rather than the page surface. It already contains the
    // theme-appropriate alpha and is shared by the app's other modal/backdrop experiences.
    overlayColor: getComputedStyle(document.documentElement).getPropertyValue("--overlay-spotlight").trim(),
    overlayOpacity: 1,
    stagePadding: 6,
    stageRadius: 12,
    popoverOffset: 14,
    popoverClass: "ibada-tour",
    nextBtnText: options.labels.next,
    prevBtnText: options.labels.prev,
    doneBtnText: options.labels.done,
    steps,
    onNextClick: () => {
      if (!instance.hasNextStep()) {
        completed = true;
        instance.destroy();
        return;
      }
      instance.moveNext();
    },
    onPrevClick: () => instance.movePrevious(),
    // Inject a "Maybe later" exit button into the footer on every step except the last (which has "Done").
    onPopoverRender: (popover) => {
      const footer = popover.footer;
      if (!footer) return;
      const existing = footer.querySelector(".ibada-tour-skip");
      if (!instance.hasNextStep()) {
        existing?.remove();
        return;
      }
      if (existing) return;
      const skip = document.createElement("button");
      skip.type = "button";
      skip.className = "ibada-tour-skip";
      skip.textContent = options.labels.skip;
      skip.addEventListener("click", () => instance.destroy());
      footer.insertBefore(skip, footer.firstChild);
    },
    onDestroyed: () => {
      if (completed) options.onComplete?.();
      else options.onSkip?.();
    }
  });

  instance.drive();
  return { destroy: () => instance.destroy() };
}
